import React, { useState, useMemo } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Loader2, Search, RefreshCw, Bell, Info } from 'lucide-react'
import { useClientNotificationSettings } from '@/hooks/useClientNotificationSettings'
import { ClientNotificationCard } from './ClientNotificationCard'
import { BulkNotificationActions } from './BulkNotificationActions'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'

export function NotificationSettingsTab() {
  const { 
    settings, 
    isLoading, 
    error, 
    refetch, 
    updateClientSettings,
    bulkUpdateSettings 
  } = useClientNotificationSettings()

  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<'all' | 'enabled' | 'disabled'>('all')

  // Filter and search logic
  const filteredSettings = useMemo(() => {
    let filtered = settings

    // Apply status filter - check if any notification group is enabled
    if (filterStatus === 'enabled') {
      filtered = filtered.filter(s => 
        s.email_replies_enabled || 
        s.linkedin_replies_enabled ||
        s.performance_reports_enabled ||
        s.outbound_reports_enabled ||
        s.meeting_booked_enabled
      )
    } else if (filterStatus === 'disabled') {
      filtered = filtered.filter(s => 
        !s.email_replies_enabled && 
        !s.linkedin_replies_enabled &&
        !s.performance_reports_enabled &&
        !s.outbound_reports_enabled &&
        !s.meeting_booked_enabled
      )
    }

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(s => 
        s.client_name.toLowerCase().includes(search) ||
        s.client_id.includes(search) ||
        s.slack_positive_replies_channel?.toLowerCase().includes(search) ||
        s.slack_neutral_negative_replies_channel?.toLowerCase().includes(search)
      )
    }

    return filtered
  }, [settings, filterStatus, searchTerm])

  const enabledCount = settings.filter(s => 
    s.email_replies_enabled || 
    s.linkedin_replies_enabled ||
    s.performance_reports_enabled ||
    s.outbound_reports_enabled ||
    s.meeting_booked_enabled
  ).length

  const totalNotifications = settings.reduce((sum, s) => sum + s.notifications_sent, 0)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[400px]">
        <div className="text-center space-y-3">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading notification settings...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Error Loading Settings</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Real-Time Notification Settings
              </CardTitle>
              <CardDescription className="mt-2">
                Control all Slack notifications: email replies, LinkedIn replies, performance reports, and outbound reports
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={refetch}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          </div>

          {/* Stats Summary */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold">{settings.length}</div>
              <div className="text-sm text-muted-foreground">Active Clients</div>
            </div>
            <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
              <div className="text-2xl font-bold text-green-600 dark:text-green-500">{enabledCount}</div>
              <div className="text-sm text-muted-foreground">Notifications Enabled</div>
            </div>
            <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-500">{totalNotifications}</div>
              <div className="text-sm text-muted-foreground">Total Sent (All Time)</div>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {/* Info Alert */}
          <Alert className="mb-4">
            <Info className="h-4 w-4" />
            <AlertTitle>How It Works</AlertTitle>
            <AlertDescription className="text-sm mt-2">
              Control all notification types per client. Enable/disable entire categories (Email Replies, LinkedIn Replies, Reports) 
              or individual notification types. Parent toggles enable/disable all child notifications. Statistics show counts per type.
            </AlertDescription>
          </Alert>

          {/* Controls */}
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search clients or channels..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Filter */}
            <Select value={filterStatus} onValueChange={(v: any) => setFilterStatus(v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients ({settings.length})</SelectItem>
                <SelectItem value="enabled">Enabled ({enabledCount})</SelectItem>
                <SelectItem value="disabled">Disabled ({settings.length - enabledCount})</SelectItem>
              </SelectContent>
            </Select>

            {/* Bulk Actions */}
            <BulkNotificationActions
              clientIds={filteredSettings.map(s => s.client_id)}
              clientNames={filteredSettings.map(s => s.client_name)}
              onBulkUpdate={bulkUpdateSettings}
              disabled={filteredSettings.length === 0}
            />
          </div>
        </CardContent>
      </Card>

      {/* Client Cards Grid */}
      {filteredSettings.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-[200px] text-center">
            <Bell className="h-12 w-12 text-muted-foreground mb-3" />
            <p className="text-lg font-medium">No clients found</p>
            <p className="text-sm text-muted-foreground mt-1">
              {searchTerm ? 'Try a different search term' : 'No active clients with this filter'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredSettings.map(setting => (
            <ClientNotificationCard
              key={setting.client_id}
              setting={setting}
              onUpdate={updateClientSettings}
            />
          ))}
        </div>
      )}
    </div>
  )
}

